import { useEffect, useState, type ReactNode } from 'react';
import { NavLink as RouterNavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Alert, AppShell, Badge, Burger, Button, Collapse, Drawer, Group, Menu, NavLink, ScrollArea, Select, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { logout, qs } from '../api/client.ts';
import { ROLE_LABEL, type Role } from '../api/types.ts';
import { useApiQuery, useAuth, useWarehouse, useWarehouses, warehouseStore } from '../lib/hooks.ts';
import { OfflineSync } from '../lib/offline/OfflineSync.tsx';
import { useRealtimeStatus } from '../realtime/RealtimeProvider.tsx';
import { Encouragement, MoodToggle } from './Encouragement.tsx';
import { helpFor } from '../pages/HelpPage.tsx';
import { ModeSwitch } from './ModeSwitch.tsx';
import { SupplierCalendarBell, SupplierCalendarReminder } from './SupplierCalendar.tsx';
import { BufferAlerts } from './BufferAlerts.tsx';

type NavItem = { to: string; label: string; roles: Role[] };
const ALL: Role[] = ['admin', 'manager', 'storekeeper', 'viewer'];
const M: Role[] = ['admin', 'manager', 'viewer'];

// Навигация по ходу товара (задание 25.09.2026): Главная → Закупки → Приёмка → Остатки →
// Заказы FBS → Поставки FBO/FBW → Справочники → Настройки. Раздел из одного пункта — просто ссылка.
// Меню — строкой сверху с выпадающими разделами (просьба 25.09.2026: не занимать место слева).
// На телефоне — кнопка-«бургер» и выезжающий список.
const NAV: { section: string; items: NavItem[] }[] = [
  { section: 'Главная', items: [{ to: '/today', label: 'Главная', roles: M }] },
  {
    section: 'Закупки',
    items: [
      { to: '/purchasing', label: 'Что заказать', roles: M },
      { to: '/supplier-calendar', label: 'Календарь заказов', roles: M },
      { to: '/purchase-orders', label: 'Заказы поставщикам', roles: M },
    ],
  },
  {
    section: 'Приёмка',
    items: [
      { to: '/receipts', label: 'Приёмка товара', roles: ALL },
      { to: '/marking', label: 'Электронные накладные (с СИ и без)', roles: ALL },
      { to: '/customer-returns', label: 'Возвраты от покупателей', roles: ALL },
    ],
  },
  {
    section: 'Остатки',
    items: [
      { to: '/stocks', label: 'Остатки', roles: ALL },
      { to: '/product-flow', label: 'Движение товара', roles: ALL },
      { to: '/inventory', label: 'Инвентаризация', roles: ALL },
      { to: '/stock-docs', label: 'Оприходование и списание', roles: ALL },
    ],
  },
  {
    section: 'Заказы FBS',
    items: [
      { to: '/orders', label: 'Заказы', roles: M },
      { to: '/my-tasks', label: 'Мои задания', roles: ALL },
      { to: '/pick-lists', label: 'Листы подбора', roles: ALL },
      { to: '/packing', label: 'Упаковка', roles: ALL },
      { to: '/shipments', label: 'Отгрузки', roles: ALL },
      { to: '/waybills', label: 'Накладные', roles: ALL },
      { to: '/order-diffs', label: 'Изменившиеся заказы', roles: M },
    ],
  },
  { section: 'Поставки FBO / FBW', items: [{ to: '/supplies', label: 'Поставки FBO / FBW', roles: ALL }] },
  {
    section: 'Справочники',
    items: [
      { to: '/products', label: 'Товары и комплекты', roles: M },
      { to: '/parties', label: 'Контрагенты', roles: M },
      { to: '/mapping', label: 'Связи с WB / Ozon', roles: M },
    ],
  },
  {
    section: 'Настройки',
    items: [
      { to: '/settings', label: 'Настройки', roles: ['admin'] },
      { to: '/tsd', label: 'Режим ТСД', roles: ['admin', 'storekeeper'] },
    ],
  },
  { section: 'Справка', items: [{ to: '/help', label: 'Справка', roles: ALL }, { to: '/manual', label: 'Инструкция менеджера', roles: ALL }] },
];

export function LiveBadge() {
  const status = useRealtimeStatus();
  const map = {
    online: { color: 'green', label: 'онлайн', hint: 'Изменения коллег появляются сразу' },
    connecting: { color: 'gray', label: 'подключение', hint: 'Соединяемся с сервером' },
    offline: { color: 'red', label: 'нет связи', hint: 'Данные на экране могут быть устаревшими — переподключаемся' },
  }[status];
  return (
    <Tooltip label={map.hint}>
      <Badge color={map.color} variant="dot" size="lg" visibleFrom="sm">{map.label}</Badge>
    </Tooltip>
  );
}

export function WarehousePicker() {
  const { user } = useAuth();
  const { data } = useWarehouses();
  const current = useWarehouse();
  if (!data || data.length < 2 || user?.warehouse_id) {
    const w = data?.find((x) => x.id === current);
    return w ? <Text size="sm" c="dimmed" visibleFrom="md">Склад: {w.name}</Text> : null;
  }
  return (
    <Select
      size="sm" w={180} allowDeselect={false} aria-label="Склад" visibleFrom="sm"
      data={data.map((w) => ({ value: String(w.id), label: w.name }))}
      value={current ? String(current) : null}
      onChange={(v) => warehouseStore.set(v ? Number(v) : null)}
    />
  );
}

/** Поиск по товару (артикул/название/штрихкод) — ведёт в «Остатки» с готовым фильтром.
 * Заказ/поставку/ячейку так найти пока нельзя — единый поиск по всем сущностям в разработке. */
function GlobalSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  return (
    <TextInput
      placeholder="Товар, артикул, штрихкод…" w={300} size="xs" visibleFrom="md"
      value={value} onChange={(e) => setValue(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' || !value.trim()) return;
        navigate(`/stocks${qs({ search: value.trim() })}`);
      }}
    />
  );
}

/** Бейдж «Центр проблем» — реальный счётчик, не декоративный ноль */
function ProblemsBell() {
  const navigate = useNavigate();
  const warehouseId = useWarehouse();
  const { data } = useApiQuery<any[]>(['problems', 'badge', warehouseId], warehouseId ? `/problems${qs({ warehouse_id: warehouseId })}` : null, { refetchInterval: 60_000 });
  const n = data?.length ?? 0;
  return (
    <Tooltip label="Центр проблем">
      <UnstyledButton onClick={() => navigate('/problems')} pos="relative" mr={6}>
        <Text fz={20} lh={1}>⚠</Text>
        {n > 0 && <Badge color="red" size="xs" circle pos="absolute" top={-6} right={-8}>{n > 99 ? '99+' : n}</Badge>}
      </UnstyledButton>
    </Tooltip>
  );
}

/** Раздел меню: сворачивается щелчком по названию, состояние помнится между заходами */
function NavSection({ title, children, defaultOpen }: { title: string; children: ReactNode; defaultOpen: boolean }) {
  const key = `nav.${title}`;
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved === null ? true : saved === 'open';
  });
  // раздел с текущей страницей всегда раскрыт
  const shown = open || defaultOpen;
  return (
    <Stack gap={2} mb={6}>
      <UnstyledButton
        className="nav-section"
        onClick={() => { const next = !shown; setOpen(next); localStorage.setItem(key, next ? 'open' : 'closed'); }}
      >
        <Group gap={6} justify="space-between" wrap="nowrap">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">{title}</Text>
          <Text size="xs" c="dimmed">{shown ? '▾' : '▸'}</Text>
        </Group>
      </UnstyledButton>
      <Collapse expanded={shown}>
        <Stack gap={2}>{children}</Stack>
      </Collapse>
    </Stack>
  );
}

/** «?» — справка по текущему экрану */
function HelpButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const s = helpFor(location.pathname);
  return (
    <Tooltip label={s ? `Справка: ${s.title}` : 'Справка'}>
      <UnstyledButton className="help-btn" onClick={() => navigate(s ? `/help/${s.id}` : '/help')} aria-label="Справка">?</UnstyledButton>
    </Tooltip>
  );
}

/** Верхнее меню: раздел из одного пункта — ссылка, иначе выпадающий список (по наведению и щелчку) */
function TopMenu({ groups }: { groups: { section: string; items: NavItem[] }[] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (to: string) => location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <Group gap={2} wrap="nowrap" className="top-menu">
      {groups.map((g) => {
        const active = g.items.some((n) => isActive(n.to));
        if (g.items.length === 1) {
          const n = g.items[0];
          return (
            <UnstyledButton key={g.section} className="top-menu-item" data-active={active || undefined} onClick={() => navigate(n.to)}>
              {g.section}
            </UnstyledButton>
          );
        }
        return (
          <Menu key={g.section} trigger="click-hover" openDelay={60} closeDelay={150} position="bottom-start" offset={2} shadow="md" withinPortal>
            <Menu.Target>
              <UnstyledButton className="top-menu-item" data-active={active || undefined}>
                {g.section} <span className="top-menu-caret">▾</span>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {g.items.map((n) => (
                <Menu.Item key={n.to} component={RouterNavLink} to={n.to} className="top-menu-drop" data-active={isActive(n.to) || undefined}>
                  {n.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        );
      })}
    </Group>
  );
}

export function Layout() {
  const { user } = useAuth();
  const [opened, { toggle, close }] = useDisclosure();
  const location = useLocation();
  const groups = NAV.map((g) => ({ ...g, items: g.items.filter((n) => user && n.roles.includes(user.role)) })).filter((g) => g.items.length);
  useEffect(() => { close(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppShell header={{ height: 80 }} padding="sm">
      <AppShell.Header className="app-header">
        <Group h={44} px="sm" justify="space-between" wrap="nowrap" className="app-header-top">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="md" size="sm" />
            <Group gap={8} wrap="nowrap">
              <div className="logo-mark">W</div>
              <Text fw={700} lh={1.1} visibleFrom="xs">MP-WMS</Text>
            </Group>
            {user?.role !== 'viewer' && <ModeSwitch />}
            <LiveBadge />
            {user?.role !== 'viewer' && <OfflineSync />}
          </Group>
          <GlobalSearch />
          <Group gap="sm" wrap="nowrap">
            <Group visibleFrom="sm"><BufferAlerts /></Group>
            <MoodToggle />
            <HelpButton />
            <ProblemsBell />
            <Group visibleFrom="md"><SupplierCalendarBell /></Group>
            <WarehousePicker />
            <Stack gap={0} visibleFrom="md" align="flex-end">
              <Text size="sm" fw={500} lh={1.2}>{user?.full_name}</Text>
              <Text size="xs" c="dimmed" lh={1.2}>{user ? ROLE_LABEL[user.role] : ''}</Text>
            </Stack>
            <Button variant="subtle" size="xs" onClick={() => void logout()}>Выйти</Button>
          </Group>
        </Group>
        <ScrollArea type="never" className="top-menu-bar" px="sm" visibleFrom="md">
          <TopMenu groups={groups} />
        </ScrollArea>
        <Group h={36} px="sm" hiddenFrom="md" className="top-menu-bar" justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed" truncate>{groups.flatMap((g) => g.items).find((n) => location.pathname.startsWith(n.to))?.label ?? ''}</Text>
          <Group gap="xs" wrap="nowrap"><Group hiddenFrom="sm"><BufferAlerts /></Group><SupplierCalendarBell /></Group>
        </Group>
      </AppShell.Header>
      <Drawer opened={opened} onClose={close} size={280} padding="sm" title="Меню" hiddenFrom="md">
        {groups.map((group) => {
          const items = group.items;
          const hasActive = items.some((n) => location.pathname.startsWith(n.to));
          if (items.length === 1) {
            const n = items[0];
            return <NavLink key={group.section} component={RouterNavLink} to={n.to} label={group.section} onClick={close}
              active={location.pathname.startsWith(n.to)} className="nav-item" mb={6} fw={600} />;
          }
          return (
            <NavSection key={group.section} title={group.section} defaultOpen={hasActive}>
              {items.map((n) => <NavLink key={n.to} component={RouterNavLink} to={n.to} label={n.label} onClick={close}
                active={location.pathname.startsWith(n.to)} className="nav-item" />)}
            </NavSection>
          );
        })}
      </Drawer>
      <AppShell.Main>
        {user?.role === 'viewer' ? <Alert color="blue" mb="sm" title="Просмотр">Доступ только на чтение. Создание, изменение и удаление данных запрещены.</Alert> : <SupplierCalendarReminder />}
        <Outlet />
        <Encouragement />
      </AppShell.Main>
    </AppShell>
  );
}
